/**
 * Client-side route solver used to keep this proof of concept demoable
 * without the backend.
 *
 * Real geocoding needs an API key, so each address is hashed into a stable
 * pseudo-coordinate instead: the same address always lands on the same point,
 * which makes the ordering deterministic and repeatable even though the
 * distances are synthetic. Swap `pseudoCoords` for a real geocoder and the
 * rest of this file keeps working unchanged.
 */

const GRID_KM = 55; // side of the imaginary city box

function hash(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function pseudoCoords(address) {
  const a = hash(address.trim().toLowerCase());
  const b = hash(`${address.trim().toLowerCase()}::y`);
  return {
    x: (a % 100000) / 100000 * GRID_KM,
    y: (b % 100000) / 100000 * GRID_KM,
  };
}

function distance(a, b) {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function pathLength(points) {
  let total = 0;
  for (let i = 0; i < points.length - 1; i += 1) {
    total += distance(points[i], points[i + 1]);
  }
  return total;
}

/** Greedy nearest-neighbour ordering of the interior stops. */
function nearestNeighbour(points) {
  const [start, ...rest] = points;
  const end = rest.pop();
  const order = [start];
  const remaining = [...rest];

  let current = start;
  while (remaining.length) {
    let bestIndex = 0;
    let bestDistance = Infinity;
    for (let i = 0; i < remaining.length; i += 1) {
      const d = distance(current, remaining[i]);
      if (d < bestDistance) {
        bestDistance = d;
        bestIndex = i;
      }
    }
    current = remaining.splice(bestIndex, 1)[0];
    order.push(current);
  }

  order.push(end);
  return order;
}

/** 2-opt refinement — cheap, and cleans up the crossings greedy leaves behind. */
function twoOpt(points) {
  const route = [...points];
  let improved = true;

  while (improved) {
    improved = false;
    for (let i = 1; i < route.length - 2; i += 1) {
      for (let k = i + 1; k < route.length - 1; k += 1) {
        const before =
          distance(route[i - 1], route[i]) + distance(route[k], route[k + 1]);
        const after =
          distance(route[i - 1], route[k]) + distance(route[i], route[k + 1]);
        if (after < before - 1e-9) {
          const segment = route.slice(i, k + 1).reverse();
          route.splice(i, segment.length, ...segment);
          improved = true;
        }
      }
    }
  }

  return route;
}

/**
 * @param {string[]} addresses [origin, ...stops, destination]
 * @returns {{ ordered: string[], legs: {from: string, to: string, km: number}[],
 *            totalKm: number, savedKm: number, minutes: number }}
 */
export default function solveRoute(addresses) {
  const points = addresses.map((address) => ({
    address,
    ...pseudoCoords(address),
  }));

  const baseline = pathLength(points);
  const optimised =
    points.length > 3 ? twoOpt(nearestNeighbour(points)) : points;
  const totalKm = pathLength(optimised);

  const legs = optimised.slice(0, -1).map((point, index) => ({
    from: point.address,
    to: optimised[index + 1].address,
    km: distance(point, optimised[index + 1]),
  }));

  return {
    ordered: optimised.map((point) => point.address),
    legs,
    totalKm,
    savedKm: Math.max(0, baseline - totalKm),
    // ~38 km/h average city pace, plus 4 minutes standing at each stop.
    minutes: Math.round((totalKm / 38) * 60 + (optimised.length - 2) * 4),
  };
}
